import { forwardRef, memo, useMemo, useRef, useState } from 'react';
import { ActionIcon, Checkbox, Grid, Paper, Select, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { DatePickerInput, DateValue } from '@mantine/dates';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useGetScheduledDeliveries } from '../../hooks/reports/queries';
import { ScheduledDeliveriesOrderData } from '../../pages/Reports/ScheduledDeliveriesReport';
import TableView, {
  buildScheduledDeliveriesColumns
} from '../../pages/Reports/ScheduledDeliveriesReport/TableView';
import { useGetStores } from '../../hooks/stores/queries';
import useDownloadExcel from '../../hooks/useDownloadExcel/useDownloadExcel';
import { IconFileSpreadsheet, IconInfoCircle, IconPrinter } from '@tabler/icons-react';
import { exportScheduledDeliveriesReportCSV } from '../../services/reports.service';
import { useReactToPrint } from 'react-to-print';

type PrintableTableProps = {
  rows: ScheduledDeliveriesOrderData[];
  columns: ReturnType<typeof buildScheduledDeliveriesColumns>;
};

const PrintableScheduledDeliveriesTable = forwardRef<HTMLDivElement, PrintableTableProps>(
({ rows, columns }, ref) => {
  return (
    <div ref={ref} className="print-only">
      <table className="print-table">
        <thead>
          <tr>
            <th>#</th>
            {columns.map((col, idx) => (
              <th key={`head-${idx}`}>{col.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={`${row.orderId || idx}`}>
              <td>{idx + 1}</td>
              {columns.map((col, colIdx) => (
                <td key={`cell-${idx}-${colIdx}`}>{col.accessorFn(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});
PrintableScheduledDeliveriesTable.displayName = 'PrintableScheduledDeliveriesTable';

export default memo(function ScheduledDeliveriesByDateReport({
  date = '',
  close: _close = () => {},
  storeId = 'all',
  companyId = ''
}: {
  date: string;
  close: () => void;
  storeId: string;
  companyId: string;
}) {
  const { t } = useTranslation(['FieldName', 'CommonButtons', 'ReportsPage']);
  const { isLoading: isGetStoresLoading, data: stores } = useGetStores(true);
  const [selectedDate, setSelectedDate] = useState<DateValue>(
    date ? moment(date).toDate() : moment().toDate()
  );
  const [selectedStoreId, setSelectedStoreId] = useState<string>(storeId || 'all');
  const [hideCompleted, setHideCompleted] = useState<boolean>(false);
  const printableColumns = useMemo(() => buildScheduledDeliveriesColumns(t), [t]);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const visiblePrintableColumns = useMemo(
    () => printableColumns.filter((col) => columnVisibility[col.id ?? ''] !== false),
    [printableColumns, columnVisibility]
  );
  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Reporte_Entregas_Programadas_${companyId}_${selectedStoreId}_${moment(
      selectedDate || date
    ).format('YYYY-MM-DD')}`
  });
  const { downloadExcelV2, loadingExport } = useDownloadExcel();

  const queryParams = useMemo(
    () => ({
      storeId: selectedStoreId || 'all',
      dateFrom: selectedDate
        ? moment(selectedDate).format('YYYY-MM-DD')
        : moment(date).format('YYYY-MM-DD'),
      dateTo: selectedDate
        ? moment(selectedDate).format('YYYY-MM-DD')
        : moment(date).format('YYYY-MM-DD'),
      includeDelivered: hideCompleted ? 'false' : 'true'
    }),
    [selectedStoreId, selectedDate, date, hideCompleted]
  );

  const {
    data,
    isLoading: isGetLoadingOrders,
    isError
  } = useGetScheduledDeliveries(companyId, queryParams);

  // Flatten all orders from all stores into a single array
  const tableInfoData: ScheduledDeliveriesOrderData[] = useMemo(() => {
    if (!data?.items) return [];
    return data.items.flatMap((store: any) =>
      (store.orders || []).map((order: any) => ({
        ...order,
        storeName: store.storeName
      }))
    );
  }, [data]);

  const totalRowsCount = useMemo(() => tableInfoData.length, [tableInfoData]);
  const summary = useMemo(() => {
    const grandTotals = data?.summary?.grandTotals || {};
    const units = data?.summary?.units || {};
    const grandTotalsByUnit = data?.summary?.grandTotalsByUnit || {};

    const weightUnit = units.weightUnit || 'kg';
    const lengthUnit = units.lengthUnit || 'm';

    const normalPieces = Number(grandTotals.normalPieces || 0);
    const weight = Number(grandTotals.weight || 0);
    const length = Number(grandTotals.length || 0);
    const weightKg = Number(grandTotalsByUnit?.weight?.kg || 0);
    const weightLb = Number(grandTotalsByUnit?.weight?.lb || 0);
    const lengthM = Number(grandTotalsByUnit?.length?.m || 0);
    const lengthFt = Number(grandTotalsByUnit?.length?.ft || 0);

    const formatQty = (value: number) =>
      new Intl.NumberFormat('es-MX', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(value);

    const weightText =
      weightUnit === 'mixed'
        ? `${formatQty(weightKg)} kg | ${formatQty(weightLb)} lb`
        : `${formatQty(weight)} ${weightUnit}`;

    const lengthText =
      lengthUnit === 'mixed'
        ? `${formatQty(lengthM)} m | ${formatQty(lengthFt)} ft`
        : `${formatQty(length)} ${lengthUnit}`;

    return {
      normalPiecesText: formatQty(normalPieces),
      weightText,
      lengthText
    };
  }, [data]);

  const storeSelectData = useMemo(() => {
    const allOption = { label: t('FieldName:allStores') || 'Todas', value: 'all' };
    const storeOptions =
      stores
        ?.filter((store) => store.name && store._id)
        .map((store) => ({
          label: store.name || '',
          value: store._id || ''
        })) || [];
    return [allOption, ...storeOptions];
  }, [stores, t]);

  return (
    <>
      <style>
        {`
          .no-print { }
          .print-only { display: none; }
          @media print {
            .no-print { display: none !important; }
            .print-only { display: block !important; }
            table.print-table {
              width: 100%;
              border-collapse: collapse;
            }
            table.print-table th,
            table.print-table td {
              border: 1px solid #dcdcdc;
              padding: 6px;
              font-size: 12px;
            }
            table.print-table th {
              background: #f5f5f5;
            }
          }
        `}
      </style>
      <Grid gutter="md">
        <Grid.Col span={{ xs: 12, md: 'auto' }} className="no-print">
          <DatePickerInput
            clearable={false}
            label={t('FieldName:date')}
            placeholder={t('FieldName:date')}
            value={selectedDate}
            onChange={setSelectedDate}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'auto' }} className="no-print">
          <Select
            value={selectedStoreId}
            onChange={(val) => setSelectedStoreId(val || 'all')}
            allowDeselect={false}
            label={t('FieldName:store')}
            placeholder={t('FieldName:store')}
            data={storeSelectData}
            disabled={isGetStoresLoading}
          />
        </Grid.Col>
        <Grid.Col
          span={{ xs: 12, md: 'auto' }}
          style={{ alignSelf: 'flex-end' }}
          className="no-print">
          <Checkbox
            label={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span>{t('ReportsPage:scheduledDeliveriesReport.hideCompleted')}</span>
                <Tooltip
                  label={t('ReportsPage:scheduledDeliveriesReport.hideCompletedTooltip')}
                  multiline
                  maw={280}
                  withArrow>
                  <ThemeIcon
                    component="span"
                    size={16}
                    radius="xl"
                    variant="transparent"
                    color="gray"
                    style={{ cursor: 'help', flexShrink: 0 }}>
                    <IconInfoCircle size={14} stroke={1.8} />
                  </ThemeIcon>
                </Tooltip>
              </span>
            }
            checked={hideCompleted}
            onChange={(e) => setHideCompleted(e.currentTarget.checked)}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }} className="no-print">
          <Grid justify="flex-start" gutter="xs">
            <Grid.Col span="content">
              <Tooltip label={t('CommonButtons:exportList')}>
                <ActionIcon
                  disabled={loadingExport}
                  onClick={() => {
                    downloadExcelV2(async () => {
                      try {
                        return await exportScheduledDeliveriesReportCSV(companyId, queryParams);
                      } catch (e) {
                        throw e;
                      }
                    }, `Reporte_Entregas_Programadas_${selectedStoreId || 'all'}`);
                  }}
                  variant="filled"
                  color="#1D6F42"
                  aria-label="Descargar_Listado_CSV">
                  <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            </Grid.Col>
            <Grid.Col span="content">
              <Tooltip label={t('CommonButtons:print')}>
                <ActionIcon
                  onClick={handlePrint}
                  variant="filled"
                  color="blue"
                  aria-label="Imprimir_Listado">
                  <IconPrinter style={{ width: '70%', height: '70%' }} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            </Grid.Col>
          </Grid>
        </Grid.Col>
        <Grid.Col span={12}>
          <Paper withBorder p="sm" radius="md">
            <Grid gutter="xs">
              <Grid.Col span={{ xs: 12, md: 4 }}>
                <Text size="sm" c="dimmed">
                  {t('ReportsPage:scheduledDeliveriesReport.summary.pieces')}
                </Text>
                <Text fw={700}>{summary.normalPiecesText}</Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 4 }}>
                <Text size="sm" c="dimmed">
                  {t('ReportsPage:scheduledDeliveriesReport.summary.weight')}
                </Text>
                <Text fw={700}>{summary.weightText}</Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 4 }}>
                <Text size="sm" c="dimmed">
                  {t('ReportsPage:scheduledDeliveriesReport.summary.length')}
                </Text>
                <Text fw={700}>{summary.lengthText}</Text>
              </Grid.Col>
            </Grid>
          </Paper>
        </Grid.Col>
        <Grid.Col span={12}>
          <TableView
            tableInfoData={tableInfoData}
            totalRowsCount={totalRowsCount}
            isError={isError}
            isLoading={isGetLoadingOrders}
            columnVisibility={columnVisibility}
            setColumnVisibility={setColumnVisibility}
          />
        </Grid.Col>
      </Grid>
      <PrintableScheduledDeliveriesTable
        ref={printRef}
        rows={tableInfoData}
        columns={visiblePrintableColumns}
      />
    </>
  );
});
